import { useEffect, useState } from 'react';
import api from '../api/axios';
import { useAuth } from '../context/AuthContext';

function Leaves() {
  const { hasPermission } = useAuth();
  const [activeTab, setActiveTab] = useState('myLeaves');
  const [leaves, setLeaves] = useState([]);
  const [holidays, setHolidays] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [showResponseModal, setShowResponseModal] = useState(false);
  const [showHolidayModal, setShowHolidayModal] = useState(false);
  const [selectedLeave, setSelectedLeave] = useState(null);
  const [editingHoliday, setEditingHoliday] = useState(null);
  const [formData, setFormData] = useState({
    leaveType: 'Casual Leave',
    startDate: '',
    endDate: '',
    reason: ''
  });
  const [responseData, setResponseData] = useState({
    status: '',
    adminResponse: ''
  });
  const [holidayFormData, setHolidayFormData] = useState({
    name: '',
    date: '',
    description: '',
    isOptional: false
  });
  
  const isAdmin = hasPermission('leaves', 'update');

  useEffect(() => {
    fetchLeaves();
    fetchHolidays();
  }, []);

  const fetchLeaves = async () => {
    try {
      const response = await api.get('/leaves');
      setLeaves(response.data);
    } catch (error) {
      console.error('Error fetching leaves:', error);
    }
  };

  const fetchHolidays = async () => {
    try {
      const response = await api.get('/leaves/holidays');
      setHolidays(response.data);
    } catch (error) {
      console.error('Error fetching holidays:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.post('/leaves', formData);
      setShowModal(false);
      setFormData({
        leaveType: 'Casual Leave',
        startDate: '',
        endDate: '',
        reason: ''
      });
      fetchLeaves();
    } catch (error) {
      alert('Error applying for leave');
    }
  };

  const handleRespond = (leave) => {
    setSelectedLeave(leave);
    setResponseData({
      status: leave.status,
      adminResponse: leave.adminResponse || ''
    });
    setShowResponseModal(true);
  };

  const handleResponseSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.put(`/leaves/${selectedLeave._id}`, responseData);
      setShowResponseModal(false);
      setSelectedLeave(null);
      setResponseData({ status: '', adminResponse: '' });
      fetchLeaves();
    } catch (error) {
      alert('Error responding to leave');
    }
  };

  const handleHolidaySubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingHoliday) {
        await api.put(`/leaves/holidays/${editingHoliday._id}`, holidayFormData);
      } else {
        await api.post('/leaves/holidays', holidayFormData);
      }
      setShowHolidayModal(false);
      setEditingHoliday(null);
      setHolidayFormData({ name: '', date: '', description: '', isOptional: false });
      fetchHolidays();
    } catch (error) {
      alert('Error saving holiday');
    }
  };

  const handleEditHoliday = (holiday) => {
    setEditingHoliday(holiday);
    setHolidayFormData({
      name: holiday.name,
      date: new Date(holiday.date).toISOString().split('T')[0],
      description: holiday.description || '',
      isOptional: holiday.isOptional
    });
    setShowHolidayModal(true);
  };

  const handleDeleteHoliday = async (id) => {
    if (window.confirm('Are you sure you want to delete this holiday?')) {
      try {
        await api.delete(`/leaves/holidays/${id}`);
        fetchHolidays();
      } catch (error) {
        alert('Error deleting holiday');
      }
    }
  };

  const handleExportExcel = async () => {
    try {
      const response = await api.get('/leaves/export/excel', {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'leaves.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      alert('Error exporting to Excel');
      console.error('Export error:', error);
    }
  };

  const handleExportPDF = async () => {
    try {
      const response = await api.get('/leaves/export/pdf', {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'leaves.pdf');
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      alert('Error exporting to PDF');
      console.error('Export error:', error);
    }
  };

  const handleExportWord = async () => {
    try {
      const response = await api.get('/leaves/export/word', {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'leaves.docx');
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      alert('Error exporting to Word');
      console.error('Export error:', error);
    }
  };

  const getDayOfWeek = (date) => {
    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    return days[new Date(date).getDay()];
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: 'badge-warning',
      approved: 'badge-success',
      rejected: 'badge-danger'
    };
    return badges[status] || 'badge-info';
  };

  return (
    <div className="container">
      <div className="flex-between mb-20">
        <h1>Leaves Management</h1>
        <div style={{ display: 'flex', gap: '10px' }}>
          {activeTab === 'myLeaves' && leaves.length > 0 && (
            <>
              <button 
                className="btn btn-secondary" 
                onClick={handleExportExcel}
                style={{ display: 'flex', alignItems: 'center', gap: '5px' }}
              >
                📊 Export Excel
              </button>
              <button 
                className="btn btn-secondary" 
                onClick={handleExportPDF}
                style={{ display: 'flex', alignItems: 'center', gap: '5px' }}
              >
                📄 Export PDF
              </button>
              <button 
                className="btn btn-secondary" 
                onClick={handleExportWord}
                style={{ display: 'flex', alignItems: 'center', gap: '5px' }}
              >
                📝 Export Word
              </button>
            </>
          )}
          {!isAdmin && (
            <button className="btn btn-primary" onClick={() => setShowModal(true)}>
              Apply Leave
            </button>
          )}
        </div>
      </div>

      <div className="card">
        <div style={{ display: 'flex', gap: '10px', borderBottom: '1px solid #ddd', marginBottom: '20px' }}>
          <button
            className={`btn ${activeTab === 'myLeaves' ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => setActiveTab('myLeaves')}
            style={{ borderRadius: '0' }}
          >
            My Leaves
          </button>
          <button
            className={`btn ${activeTab === 'holidays' ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => setActiveTab('holidays')}
            style={{ borderRadius: '0' }}
          >
            Holidays
          </button>
        </div>

        {activeTab === 'myLeaves' && (
          <table className="table">
            <thead>
              <tr>
                {isAdmin && <th>Employee</th>}
                <th>Leave Type</th>
                <th>Start Date</th>
                <th>End Date</th>
                <th>Reason</th>
                <th>Status</th>
                {isAdmin && <th>Actions</th>}
              </tr>
            </thead>
            <tbody>
              {leaves.map((leave) => (
                <tr key={leave._id}>
                  {isAdmin && (
                    <td>
                      {leave.employee?.firstName} {leave.employee?.lastName}
                      <br />
                      <small style={{ color: '#666' }}>({leave.employee?.employeeId})</small>
                    </td>
                  )}
                  <td>{leave.leaveType}</td>
                  <td>{new Date(leave.startDate).toLocaleDateString()}</td>
                  <td>{new Date(leave.endDate).toLocaleDateString()}</td>
                  <td>{leave.reason}</td>
                  <td>
                    <span className={`badge ${getStatusBadge(leave.status)}`}>
                      {leave.status}
                    </span>
                    {leave.adminResponse && (
                      <div style={{ marginTop: '5px', fontSize: '12px', color: '#666' }}>
                        <strong>Response:</strong> {leave.adminResponse.substring(0, 50)}
                        {leave.adminResponse.length > 50 && '...'}
                      </div>
                    )}
                  </td>
                  {isAdmin && (
                    <td>
                      <button 
                        className="btn btn-primary" 
                        onClick={() => handleRespond(leave)}
                        style={{ padding: '6px 12px', fontSize: '12px' }}
                      >
                        Respond
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {activeTab === 'holidays' && (
          <>
            {isAdmin && (
              <div style={{ marginBottom: '20px' }}>
                <button className="btn btn-primary" onClick={() => {
                  setEditingHoliday(null);
                  setHolidayFormData({ name: '', date: '', description: '', isOptional: false });
                  setShowHolidayModal(true);
                }}>
                  Add Holiday
                </button>
              </div>
            )}
            <table className="table">
              <thead>
                <tr>
                  <th>Holiday Name</th>
                  <th>Date</th>
                  <th>Day</th>
                  <th>Occasion</th>
                  <th>Type</th>
                  {isAdmin && <th>Actions</th>}
                </tr>
              </thead>
              <tbody>
                {holidays.map((holiday) => (
                  <tr key={holiday._id}>
                    <td style={{ fontWeight: '500' }}>{holiday.name}</td>
                    <td>{new Date(holiday.date).toLocaleDateString()}</td>
                    <td>
                      <span style={{ 
                        padding: '4px 8px', 
                        background: '#e9ecef', 
                        borderRadius: '4px',
                        fontSize: '12px',
                        fontWeight: '500'
                      }}>
                        {getDayOfWeek(holiday.date)}
                      </span>
                    </td>
                    <td>{holiday.description || 'N/A'}</td>
                    <td>
                      <span className={`badge ${holiday.isOptional ? 'badge-warning' : 'badge-success'}`}>
                        {holiday.isOptional ? 'Optional' : 'Mandatory'}
                      </span>
                    </td>
                    {isAdmin && (
                      <td>
                        <div style={{ display: 'flex', gap: '8px' }}>
                          <button 
                            className="btn btn-secondary" 
                            onClick={() => handleEditHoliday(holiday)}
                            style={{ padding: '6px 12px', fontSize: '12px' }}
                          >
                            Edit
                          </button>
                          <button 
                            className="btn" 
                            onClick={() => handleDeleteHoliday(holiday._id)}
                            style={{ padding: '6px 12px', fontSize: '12px', background: '#dc3545', color: 'white' }}
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}

        {activeTab === 'myLeaves' && leaves.length === 0 && (
          <p className="text-center">No leave records found</p>
        )}
        
        {activeTab === 'holidays' && holidays.length === 0 && (
          <p className="text-center">No holidays configured</p>
        )}
      </div>

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">Apply Leave</h2>
              <button className="close-btn" onClick={() => setShowModal(false)}>&times;</button>
            </div>
            
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Leave Type</label>
                <select
                  className="form-control"
                  value={formData.leaveType}
                  onChange={(e) => setFormData({ ...formData, leaveType: e.target.value })}
                >
                  <option value="Sick Leave">Sick Leave</option>
                  <option value="Casual Leave">Casual Leave</option>
                  <option value="Annual Leave">Annual Leave</option>
                  <option value="Maternity Leave">Maternity Leave</option>
                  <option value="Paternity Leave">Paternity Leave</option>
                  <option value="Other">Other</option>
                </select>
              </div>
              
              <div className="form-group">
                <label>Start Date</label>
                <input
                  type="date"
                  className="form-control"
                  value={formData.startDate}
                  onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                  required
                />
              </div>
              
              <div className="form-group">
                <label>End Date</label>
                <input
                  type="date"
                  className="form-control"
                  value={formData.endDate}
                  onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                  required
                />
              </div>
              
              <div className="form-group">
                <label>Reason</label>
                <textarea
                  className="form-control"
                  value={formData.reason}
                  onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
                  required
                />
              </div>
              
              <div className="flex-end">
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  Apply
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Admin Response Modal */}
      {showResponseModal && selectedLeave && (
        <div className="modal-overlay" onClick={() => setShowResponseModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">Respond to Leave Request</h2>
              <button className="close-btn" onClick={() => setShowResponseModal(false)}>&times;</button>
            </div>
            
            <div style={{ marginBottom: '20px', padding: '15px', background: '#f8f9fa', borderRadius: '5px' }}>
              <p><strong>Employee:</strong> {selectedLeave.employee?.firstName} {selectedLeave.employee?.lastName}</p>
              <p><strong>Leave Type:</strong> {selectedLeave.leaveType}</p>
              <p><strong>Period:</strong> {new Date(selectedLeave.startDate).toLocaleDateString()} - {new Date(selectedLeave.endDate).toLocaleDateString()}</p>
              <p><strong>Reason:</strong> {selectedLeave.reason}</p>
            </div>
            
            <form onSubmit={handleResponseSubmit}>
              <div className="form-group">
                <label>Status *</label>
                <select
                  className="form-control"
                  value={responseData.status}
                  onChange={(e) => setResponseData({ ...responseData, status: e.target.value })}
                  required
                >
                  <option value="">Select Status</option>
                  <option value="pending">Pending</option>
                  <option value="approved">Approved</option>
                  <option value="rejected">Rejected</option>
                </select>
              </div>
              
              <div className="form-group">
                <label>Admin Response</label>
                <textarea
                  className="form-control"
                  value={responseData.adminResponse}
                  onChange={(e) => setResponseData({ ...responseData, adminResponse: e.target.value })}
                  placeholder="Provide feedback or additional information to the employee"
                  rows="4"
                />
              </div>
              
              <div className="flex-end">
                <button type="button" className="btn btn-secondary" onClick={() => setShowResponseModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  Submit Response
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add/Edit Holiday Modal */}
      {showHolidayModal && (
        <div className="modal-overlay" onClick={() => setShowHolidayModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">{editingHoliday ? 'Edit Holiday' : 'Add Holiday'}</h2>
              <button className="close-btn" onClick={() => setShowHolidayModal(false)}>&times;</button>
            </div>
            
            <form onSubmit={handleHolidaySubmit}>
              <div className="form-group">
                <label>Holiday Name *</label>
                <input
                  type="text"
                  className="form-control"
                  value={holidayFormData.name}
                  onChange={(e) => setHolidayFormData({ ...holidayFormData, name: e.target.value })}
                  required
                  placeholder="e.g., Diwali, Christmas"
                />
              </div>
              
              <div className="form-group">
                <label>Date *</label>
                <input
                  type="date"
                  className="form-control"
                  value={holidayFormData.date}
                  onChange={(e) => setHolidayFormData({ ...holidayFormData, date: e.target.value })}
                  required
                />
                {holidayFormData.date && (
                  <small style={{ color: '#666', marginTop: '5px', display: 'block' }}>
                    Day: {getDayOfWeek(holidayFormData.date)}
                  </small>
                )}
              </div>
              
              <div className="form-group">
                <label>Occasion/Description</label>
                <textarea
                  className="form-control"
                  value={holidayFormData.description}
                  onChange={(e) => setHolidayFormData({ ...holidayFormData, description: e.target.value })}
                  placeholder="Brief description of the holiday"
                  rows="3"
                />
              </div>
              
              <div className="form-group">
                <div className="checkbox-container">
                  <input
                    type="checkbox"
                    checked={holidayFormData.isOptional}
                    onChange={(e) => setHolidayFormData({ ...holidayFormData, isOptional: e.target.checked })}
                  />
                  <label>Optional Holiday</label>
                </div>
              </div>
              
              <div className="flex-end">
                <button type="button" className="btn btn-secondary" onClick={() => {
                  setShowHolidayModal(false);
                  setEditingHoliday(null);
                }}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  {editingHoliday ? 'Update' : 'Add'} Holiday
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default Leaves;
